import React from "react";
import { useNavigate } from "react-router-dom";

interface EnhancedLoadingIndicatorProps {
  mode?: boolean;
}

const EnhancedLoadingIndicator: React.FC<EnhancedLoadingIndicatorProps> = ({
  mode = false,
}) => {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center justify-center min-h-[600px] px-4">
      {/* Enhanced Spinner */}
      <div className="relative mb-8">
        {/* Outer rotating ring */}
        <div className="relative w-24 h-24">
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-500 border-r-indigo-500 animate-spin"></div>
          <div
            className="absolute inset-2 rounded-full border-4 border-transparent border-b-purple-500 border-l-blue-400 animate-spin"
            style={{ animationDirection: "reverse", animationDuration: "1.5s" }}
          ></div>

          {/* Center pulse */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 rounded-full animate-pulse shadow-lg"></div>
          </div>

          {/* Glow effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600 blur-2xl opacity-30 animate-pulse"></div>
        </div>

        {/* Orbiting dots */}
        <div
          className="absolute inset-0 animate-spin"
          style={{ animationDuration: "3s" }}
        >
          <div className="absolute top-0 left-1/2 w-3 h-3 bg-blue-500 rounded-full shadow-lg -translate-x-1/2"></div>
        </div>
        <div
          className="absolute inset-0 animate-spin"
          style={{ animationDuration: "3s", animationDelay: "1s" }}
        >
          <div className="absolute top-0 left-1/2 w-3 h-3 bg-indigo-500 rounded-full shadow-lg -translate-x-1/2"></div>
        </div>
        <div
          className="absolute inset-0 animate-spin"
          style={{ animationDuration: "3s", animationDelay: "2s" }}
        >
          <div className="absolute top-0 left-1/2 w-3 h-3 bg-purple-500 rounded-full shadow-lg -translate-x-1/2"></div>
        </div>
      </div>

      {/* Content without box */}
      <div className="max-w-lg w-full text-center">
        {!mode ? (
          <>
            <h3 className="text-3xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Loading your data
            </h3>
            <p className="text-lg leading-relaxed mb-2" style={{ color: "var(--text-secondary)" }}>
              Please wait while we fetch and prepare the required information.
            </p>
            <p className="text-base" style={{ color: "var(--text-tertiary)" }}>
              This may take a few moments…
            </p>
          </>
        ) : (
          <>
            <h3 className="text-3xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              AI Processing in Progress
            </h3>
            <p className="text-lg leading-relaxed mb-2" style={{ color: "var(--text-secondary)" }}>
              This may take approximately{" "}
              <span className="font-semibold text-blue-600">10–15 minutes</span>
              .
            </p>
            <p className="text-base" style={{ color: "var(--text-tertiary)" }}>
              We're securely analyzing your data and preparing actionable
              insights.
            </p>
          </>
        )}
      </div>

      {/* Button */}
      {mode && (
        <button
          onClick={() => {
            navigate("/measurement/synthetic_control_list");
          }}
          className="mt-8 px-10 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-r from-blue-600 to-indigo-600 border-0 hover:from-blue-700 hover:to-indigo-700 transform hover:scale-105 text-white font-semibold text-base"
        >
          Go to Dashboard
        </button>
      )}
    </div>
  );
};

export default EnhancedLoadingIndicator;
